import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Descriptions, Spin, Tag, Image, Row, Col, Space } from '@/shared/ui/primitives';
import { LinkOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { publicRoomWantedUrl } from '@/shared/utils/publicLinks';
import { useRoomWantedDetails } from '../hooks/useRoomWantedDetails';
import { formatDateTime, formatAdPrice } from '@/shared/utils/formatters';
import { getAdImageOrDefault } from '@/shared/assets/defaultAdImages';
import PhoneActionLinks from '@/shared/components/PhoneActionLinks';
import { useGeoDistrictOptions } from '@/shared/ui/entity-selects';
import AiAdWhatsAppUpdatesForAd from '@/features/ai-ads/components/AiAdWhatsAppUpdatesForAd';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
  REJECTED: 'error',
};

export default function RoomWantedDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useRoomWantedDetails(id);
  const { options: districtOptions } = useGeoDistrictOptions(data?.cityId);

  if (isLoading) return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  if (!data) return <Card><p>{t('roomWanted.notFound')}</p></Card>;
  const hasImages = Array.isArray(data.images) && data.images.length > 0;

  return (
    <div>
      <PageHeader back
        title={t('roomWanted.detailsTitle')}
        subtitle={`#${data.id}: ${data.title || ''}`}
        action={
          <Space>
            <Button
              icon={<LinkOutlined />}
              data-testid="roomwanted-detail-public-link"
              onClick={() => window.open(publicRoomWantedUrl(data.id), '_blank', 'noopener,noreferrer')}
            >
              {t('common.viewOnSite', { defaultValue: 'View on site' })}
            </Button>
            <Button type="primary" data-testid="roomwanted-detail-edit" onClick={() => navigate(`/room-wanted/${id}/edit`)}>
              {t('common.edit')}
            </Button>
          </Space>
        }
      />

      <AiAdWhatsAppUpdatesForAd targetType="ROOM_WANTED_AD" targetAdId={data.id} />

      {/* General */}
      <Card title={t('roomWanted.general')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
          <Descriptions.Item label={t('common.id')}>{data.id}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.status')}>
            <Tag color={STATUS_COLORS[data.status] || 'default'}>{t(`enum.${data.status}`, { defaultValue: data.status })}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.budget')}>{formatAdPrice(data.budget, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.smoking')}>{displayEnum(data.smokingPreference, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.rentType')}>{displayEnum(data.rentType, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.roomType')}>{displayEnum(data.roomType, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.daysAvailable')}>{displayEnum(data.daysAvailableOption, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.minStay')}>{displayEnum(data.minimumStayOption, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.maxStay')}>{displayEnum(data.maximumStayOption, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.availableFrom')}>{data.availableFrom ? formatDateTime(data.availableFrom) : '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.featured')}>
            <Tag color={data.isFeatured ? 'purple' : 'default'}>{data.isFeatured ? t('common.yes') : t('common.no')}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.showPhone')}>{data.showPhoneNumber ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.showWhatsapp')}>{data.showWhatsappContact ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.showOwnerInfo')}>{data.showOwnerInfo ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.showInAppMessaging')}>{data.showInAppMessaging ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.phoneNumber')}>
            <PhoneActionLinks phonePrefix={data.phonePrefix} phoneNumber={data.phoneNumber} />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.city')}>{data.cityName ? `${data.cityName} (#${data.cityId})` : '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.districts')}>{formatDistricts(data, districtOptions, t, i18n.language)}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.allDistricts')}>{data.isSelectAllDistricts ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomWanted.user')}>{data.userName ? `${data.userName} (#${data.userId})` : '—'}</Descriptions.Item>
        </Descriptions>
        {data.description && <p style={{ marginTop: 16 }}>{data.description}</p>}
        {data.rejectReason && <p style={{ marginTop: 8, color: 'var(--error)' }}>{t('roomWanted.rejectReason')}: {data.rejectReason}</p>}
      </Card>

      {/* Interests */}
      {data.interests?.length > 0 && (
        <Card title={t('roomWanted.interests')} style={{ marginBottom: 16 }}>
          {data.interests.map((i: string) => (
            <Tag key={i} color="purple" style={{ marginBottom: 4 }}>{displayEnum(i, t)}</Tag>
          ))}
        </Card>
      )}

      {/* Amenities */}
      {data.amenities?.length > 0 && (
        <Card title={`${t('roomWanted.amenities')} (${data.amenities.length})`} style={{ marginBottom: 16 }}>
          {data.amenities.map((a: any) => (
            <Tag key={a.id} color="blue" style={{ marginBottom: 4 }}>{a.nameEn}</Tag>
          ))}
        </Card>
      )}

      <Card title={t('roomWanted.preferredRoommates')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label={t('roommatePreferences.maleCount')}>{data.preferredRoommates?.maleRoommatesCount ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.femaleCount')}>{data.preferredRoommates?.femaleRoommatesCount ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.ageRange')}>{data.preferredRoommates ? `${data.preferredRoommates.minAge ?? '—'} - ${data.preferredRoommates.maxAge ?? '—'}` : '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.nationality')}>{data.preferredRoommates?.nationalityName || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.gender')}>{displayEnum(data.preferredRoommates?.genderPreference, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.smoking')}>{displayEnum(data.preferredRoommates?.smokingPreference, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.occupation')}>{displayEnum(data.preferredRoommates?.occupationType, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roommatePreferences.language')}>{displayEnum(data.preferredRoommates?.languagePreference, t)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Images */}
      <Card title={`${t('roomWanted.images')} (${hasImages ? data.images.length : 1})`} style={{ marginBottom: 16 }}>
        {hasImages ? (
          <Image.PreviewGroup>
            <Row gutter={[16, 16]}>
              {data.images.map((img: any) => (
                <Col key={img.id} xs={12} sm={8} md={6}>
                  <div style={{ position: 'relative' }}>
                    <Image
                      src={img.imageUrl}
                      alt={t('roomWanted.imageAlt', { position: img.position })}
                      width="100%"
                      height={128}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                    />
                    {img.isPrimary && (
                      <Tag color="blue" style={{ position: 'absolute', top: 4, left: 4 }}>{t('roomWanted.primary')}</Tag>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Image.PreviewGroup>
        ) : (
          <Image
            src={getAdImageOrDefault(data)}
            alt={t('roomWanted.defaultImageAlt')}
            width={192}
            height={128}
            style={{ objectFit: 'cover', borderRadius: 8 }}
          />
        )}
      </Card>

      {/* Timestamps */}
      <Card>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label={t('common.createdAt')}>{formatDateTime(data.createdAt)}</Descriptions.Item>
          <Descriptions.Item label={t('common.updatedAt')}>{formatDateTime(data.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
}

function displayEnum(value: string | null | undefined, t: (key: string, options?: any) => string) {
  return value ? t(`enum.${value}`, { defaultValue: value }) : '—';
}

function formatDistricts(data: any, districtOptions: any[], t: (key: string, options?: any) => string, language: string) {
  if (data.isSelectAllDistricts) {
    return data.cityName
      ? `${t('roomWanted.allDistricts')} - ${data.cityName} (#${data.cityId})`
      : t('roomWanted.allDistricts');
  }
  const ids = Array.isArray(data.selectedDistricts) ? data.selectedDistricts : [];
  if (!ids.length) return '—';
  const optionById = new Map((districtOptions || []).map((option: any) => [Number(option.value), option]));
  return ids.map((id: any) => {
    const option = optionById.get(Number(id));
    if (!option) return `#${id}`;
    const isAr = language?.toLowerCase().startsWith('ar');
    return isAr ? option.nameAr || option.nameEn || option.label : option.nameEn || option.nameAr || option.label;
  }).join(', ');
}
